import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BoxV2 as Box, Flex } from 'portal-commons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface MenuItemConfig<T> {
  label: string;
  onClick: (data: T) => Promise<void> | void;
  disabled?: boolean;
  hidden?: boolean;
  [key: string]: any;
}

interface MoreButtonProps<T> {
  data: T;
  items: MenuItemConfig<T>[];
  disabled?: boolean;
}

let idCounter = 0;

const MoreButton = <T,>({
  data,
  items,
  disabled = false,
  ...restProps
}: MoreButtonProps<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLUListElement>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [menuId] = useState(() => `more-menu-${++idCounter}`);
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  // Dynamically calculate the fixed coordinates for the menu to appear
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  };

  useEffect(() => {
    if (!isMenuVisible) return;

    // Close the menu when clicking outside the menu or button
    const handleClickOutside = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsMenuVisible(false);
      }
    };

    // Close the menu when scrolling occurs (prevents the menu from scrolling away from the button)
    const handleScroll = (e: Event) => {
      if (
        userMenuRef.current &&
        userMenuRef.current.contains(e.target as Node)
      ) {
        return;
      }
      setIsMenuVisible(false);
    };

    document.addEventListener('scroll', handleScroll, true); // 'true' is used to intercept all overflow scrolls in the capture phase
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePosition);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isMenuVisible]);

  return (
    <>
      <Flex
        ref={buttonRef}
        data-testid="moreButton"
        as="button"
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-expanded={isMenuVisible}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (!isMenuVisible) {
            updatePosition();
            setIsMenuVisible(true);
            setTimeout(() => userMenuRef.current?.focus(), 0);
          } else {
            setIsMenuVisible(false);
          }
        }}
        sx={{
          position: 'relative',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignSelf: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          outline: 'none',
        }}
        {...restProps}
      >
        <FontAwesomeIcon
          icon={faEllipsisVertical}
          style={{
            cursor: `${disabled ? 'not-allowed' : 'pointer'}`,
            color: `${disabled ? '#CCCCCC' : '#333E41'}`,
            width: 20,
            height: 20,
          }}
        />
        {/* Render the menu to the bottom of document.body using Portal to avoid all overflow: hidden/auto issues */}
        {typeof document !== 'undefined' &&
          createPortal(
            <Box
              as="ul"
              ref={userMenuRef}
              id={menuId}
              role="menu"
              data-testid="moreButtonMenu"
              tabIndex={-1}
              sx={{
                position: 'fixed',
                paddingY: items.length ? '8px' : 0,
                paddingX: 0,
                right: coords.right,
                top: coords.top,
                minWidth: '146px',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                overflow: 'auto',
                zIndex: 9999, // Set to highest to ensure it overlays all Modal and Table interfaces
                opacity: isMenuVisible ? 1 : 0,
                pointerEvents: isMenuVisible ? undefined : 'none',
                transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                outline: 'none',
              }}
              onClick={(e: React.MouseEvent<HTMLUListElement>) => {
                e.stopPropagation();
                setIsMenuVisible(false); // Close the menu directly here, replacing the removed onBlur
              }}
            >
              {items.map((item) => {
                return (
                  !item.hidden && (
                    <Box
                      as="li"
                      role="menuitem"
                      aria-disabled={item.disabled}
                      onClick={
                        item.disabled ? undefined : () => item.onClick(data)
                      }
                      key={item.label}
                      data-testid="menu-item"
                      sx={{
                        width: '100%',
                        p: '8px 12px',
                        listStyle: 'none',
                        textAlign: 'left',
                        color: item.disabled ? '#808789' : '#333E41',
                        background: item.disabled ? '#E6E7E7' : '#FFFFFF',
                        cursor: item.disabled ? 'not-allowed' : 'pointer',
                        '&:hover': {
                          background: item.disabled ? '#E6E7E7' : '#CCCFD0',
                        },
                      }}
                    >
                      {item.label}
                    </Box>
                  )
                );
              })}
            </Box>,
            document.body
          )}
      </Flex>
    </>
  );
};

export default MoreButton;
